/**
 * The 17 canonical membership states per the Zone Auth + Account Lifecycle Brief.
 * Server is the source of truth — do not invent new states client-side.
 *
 * `trial_returned` is the trial-path return terminal (warehouse receipt of a
 * `trial_returning` membership). The backend keeps it DISTINCT from the prepaid
 * `returned` (which got a $348 refund). Both must surface in the Users "Returned"
 * tab — see matchesUserTab in features/user-search/utils.ts.
 */
export const MEMBERSHIP_STATES = [
  "trial_active",
  "trial_ending_soon",
  "trial_returning",
  "trial_returned",
  "trial_card_failed",
  "trial_charged_off",
  "pending_shipment",
  "pending_delivery",
  "active",
  "renewing_soon",
  "past_due",
  "paused",
  "cancelled",
  "expired",
  "returned",
  "suspended",
  // WF-55 terminal: a LOST chargeback (or a confirmed ToS/fraud case) closes the
  // account for good. Sign-in is blocked permanently — see the gateway's
  // SIGNIN_BLOCKED_STATES. Distinct from `suspended`, which is recoverable.
  "permanently_closed",
] as const;

export type MembershipState = (typeof MEMBERSHIP_STATES)[number];

export type MembershipTone = "ok" | "info" | "warn" | "crit" | "beta" | "gold" | "mute";

export const MEMBERSHIP_STATE_TONE: Record<MembershipState, MembershipTone> = {
  trial_active: "info",
  trial_ending_soon: "warn",
  trial_returning: "mute",
  trial_returned: "mute",
  trial_card_failed: "crit",
  trial_charged_off: "crit",
  pending_shipment: "info",
  pending_delivery: "info",
  active: "ok",
  renewing_soon: "info",
  past_due: "crit",
  paused: "warn",
  cancelled: "mute",
  expired: "mute",
  returned: "mute",
  suspended: "crit",
  permanently_closed: "crit",
};

export const MEMBERSHIP_STATE_LABEL: Record<MembershipState, string> = {
  trial_active: "Trial active",
  trial_ending_soon: "Trial ending soon",
  trial_returning: "Trial returning",
  trial_returned: "Trial returned",
  trial_card_failed: "Trial card failed",
  trial_charged_off: "Trial charged off",
  pending_shipment: "Pending shipment",
  pending_delivery: "Pending delivery",
  active: "Active",
  renewing_soon: "Renewing soon",
  past_due: "Past due",
  paused: "Paused",
  cancelled: "Cancelled",
  expired: "Expired",
  returned: "Returned",
  suspended: "Suspended",
  permanently_closed: "Permanently closed",
};

/**
 * A user with NO membership row at all. The gateway sends this deliberately —
 * `membershipState: null` with `source: "not_configured"` — for accounts that
 * never completed checkout. It is NOT one of the 17 canonical states above and
 * must never be added to MEMBERSHIP_STATES: it is the absence of a state.
 *
 * It gets an explicit label because the API seam used to coerce it to "active",
 * showing support a green ACTIVE pill for an account that had never bought
 * anything (staging user 331, 2026-09-07). Rendering it as a blank pill would
 * be only marginally better — support needs to be told which case it is.
 */
export const NO_MEMBERSHIP_LABEL = "No membership";
export const NO_MEMBERSHIP_TONE: MembershipTone = "mute";
